import React, { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';

// Staff Discounts — BK, 11/4/26. Staff-applied discounts to non-members (a
// manual price override at the desk or in the PlayByPoint admin), found from
// PlayByPoint's payments (initiated_by = staff). Rules, limits and the YTD
// numbers behind this are in AzureApp/src/staffDiscountFlags.js.
//
// Important limit, shown on the page: PlayByPoint does not say WHICH staff
// member applied a discount, so this finds the pattern, it cannot name an
// employee. Match the time to the shift schedule or look it up in PlayByPoint admin.

const CLUB_NAMES = { LEW: 'Lewisville', MCK: 'McKinney', GB: 'Green Bay', MF: 'Menomonee Falls', WA: 'West Allis', DEN: 'Denton' };
const BUCKET_LABEL = { unexplained: 'Needs review', former_member: 'Needs review (former member)', welld: 'Welld', joined_soon: 'Joined within 7 days after', joined_later: 'Joined later', private_or_staff: 'Private event / tagged staff' };
const WELLD_LABEL = { active: 'Welld (active)', expired: 'Welld plan expired', upcoming: 'Welld plan started after', eligible: 'Welld-eligible, no plan in PlayByPoint' };
const usd = (cents) => '$' + Math.round((cents || 0) / 100).toLocaleString();
const usd2 = (cents) => '$' + ((cents || 0) / 100).toFixed(2);
const weekLabel = (iso) => new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const when = (iso) => new Date(iso).toLocaleString('en-US', { timeZone: 'America/Chicago', weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

const pill = (active) => ({
  background: active ? 'var(--yellow)' : 'var(--surface-2)',
  color: active ? '#000' : 'var(--text-muted)',
  border: '1px solid var(--border)', borderRadius: 6,
  padding: '4px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
});

// ── Daily / weekly download ─────────────────────────────────────────────────
const CT_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit' });
const CT_TIME = new Intl.DateTimeFormat('en-GB', { timeZone: 'America/Chicago', hour: '2-digit', minute: '2-digit', hour12: false });
const CT_DOW = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', weekday: 'short' });
const chicagoToday = () => CT_DAY.format(new Date());
const addDays = (iso, n) => { const d = new Date(iso + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const dayOfWeek = (iso) => new Date(iso + 'T12:00:00Z').getUTCDay(); // 0 = Sunday

function presetRange(key) {
  const t = chicagoToday();
  if (key === 'yesterday') { const y = addDays(t, -1); return [y, y]; }
  if (key === 'last7') return [addDays(t, -7), addDays(t, -1)];
  // last full Monday-Sunday week
  const mondayThisWeek = addDays(t, -((dayOfWeek(t) + 6) % 7));
  const start = addDays(mondayThisWeek, -7);
  return [start, addDays(start, 6)];
}

const csvCell = (v) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
};

function paymentsToCsv(payments) {
  const head = ['Date', 'Time (CT)', 'Day', 'Club', 'Customer', 'Email', 'Category', 'Item', 'Tender', 'Blank check',
    'List $', 'Discount $', 'Charged $', '% off', 'Coupon', 'Membership on that day', 'Review bucket', 'Welld', 'Welld plan ended', 'Joined after (days)', 'Former member', 'Review status', 'Reviewer note'];
  const cls = { never: 'Never a member', former_or_future: 'Not a member that day', member_comp: 'Comped member', member_paid: 'Paid member', trial: 'Trial', unknown: 'Unknown' };
  const lines = [head.map(csvCell).join(',')];
  payments.forEach((p) => {
    const d = new Date(p.paidAt);
    const list = (p.listCents || 0) / 100;
    lines.push([
      CT_DAY.format(d), CT_TIME.format(d), CT_DOW.format(d), CLUB_NAMES[p.clubId] || p.clubId, p.name || '(not in COMS)', p.email || '',
      p.category, p.item, p.tender, p.blankCheck ? 'yes' : '', list.toFixed(2), (p.discountCents / 100).toFixed(2),
      ((p.chargedCents || 0) / 100).toFixed(2), list ? Math.round((100 * p.discountCents) / (p.listCents || 1)) + '%' : '', p.coupon || '',
      cls[p.membershipClass] || p.membershipClass, BUCKET_LABEL[p.bucket] || p.bucket || '',
      WELLD_LABEL[p.welldStatus] || '', p.welldEndedOn || '', p.joinedAfterDays === null || p.joinedAfterDays === undefined ? '' : Number(p.joinedAfterDays).toFixed(1),
      p.priorMember ? 'yes' : '', p.reviewStatus, p.note || '',
    ].map(csvCell).join(','));
  });
  return lines.join('\r\n');
}

function saveCsv(filename, text) {
  const blob = new Blob(['﻿' + text], { type: 'text/csv;charset=utf-8' }); // BOM so Excel reads it as UTF-8
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function DownloadPanel({ clubId, get }) {
  const [period, setPeriod] = useState('lastweek');
  const [klass, setKlass] = useState('nonmember');
  const [custom, setCustom] = useState({ since: addDays(chicagoToday(), -7), until: addDays(chicagoToday(), -1) });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [since, until] = period === 'custom' ? [custom.since, custom.until] : presetRange(period);

  async function run(clubIds) {
    setBusy(true); setMsg(null);
    try {
      const parts = [];
      for (const c of clubIds) {
        const r = await get(`/revenue/staff-discounts/export?club=${c}&since=${since}&until=${until}&class=${klass}`);
        saveCsv(`staff-discounts_${c}_${since}_to_${until}.csv`, paymentsToCsv(r.payments));
        parts.push(`${CLUB_NAMES[c] || c}: ${r.total}`);
        if (clubIds.length > 1) await new Promise((res) => setTimeout(res, 600));
      }
      setMsg(`Downloaded ${parts.join(' · ')} payments`);
    } catch (e) {
      setMsg('Download failed: ' + e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ margin: '18px 0', padding: '12px 14px', background: 'var(--surface-2, #111)', border: '1px solid var(--border, #222)', borderRadius: 8 }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)', marginBottom: 8 }}>Download (one file per club, date and time on every row)</div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {[['yesterday', 'Yesterday'], ['last7', 'Last 7 days'], ['lastweek', 'Last full week (Mon–Sun)'], ['custom', 'Custom']].map(([k, label]) => (
            <button key={k} style={pill(period === k)} onClick={() => setPeriod(k)}>{label}</button>
          ))}
        </div>
        {period === 'custom' && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
            <input type="date" className="field-input" value={custom.since} max={custom.until} onChange={(e) => setCustom((c) => ({ ...c, since: e.target.value }))} />
            to
            <input type="date" className="field-input" value={custom.until} min={custom.since} onChange={(e) => setCustom((c) => ({ ...c, until: e.target.value }))} />
          </div>
        )}
        <select className="field-input" value={klass} onChange={(e) => setKlass(e.target.value)} style={{ fontSize: 12 }}>
          <option value="nonmember">Non-members</option>
          <option value="comped">Comped members</option>
          <option value="all">All discounted payments</option>
        </select>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button disabled={busy || !since || !until} onClick={() => run([clubId])}
          style={{ background: 'var(--yellow)', color: '#000', border: 'none', borderRadius: 6, padding: '6px 16px', fontSize: 12, fontWeight: 700, cursor: busy ? 'wait' : 'pointer' }}>
          {busy ? 'Preparing…' : `⬇ ${CLUB_NAMES[clubId] || clubId} (${since === until ? since : `${since} to ${until}`})`}
        </button>
        <button disabled={busy || !since || !until} onClick={() => run(Object.keys(CLUB_NAMES))}
          style={{ background: 'var(--surface-3, #1a1a1a)', color: 'var(--text)', border: '1px solid var(--border, #333)', borderRadius: 6, padding: '6px 16px', fontSize: 12, fontWeight: 600, cursor: busy ? 'wait' : 'pointer' }}>
          ⬇ All six clubs (six files)
        </button>
        {msg && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{msg}</span>}
      </div>
      <div style={{ fontSize: 10, color: '#666', marginTop: 8 }}>
        Includes private events and tagged staff with a Flag column (nothing is hidden in the file). If your browser asks, allow multiple downloads for the all-clubs option.
      </div>
    </div>
  );
}

function Overview({ data }) {
  const weeks = data?.weeks || [];
  const clubIds = Object.keys(data?.clubs || {}).sort();
  if (!clubIds.length) {
    return <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', padding: '12px 0' }}>
      No discounted payments stored yet. The nightly job fills this in; the year-to-date backfill is run once by hand.
    </div>;
  }
  return (
    <div style={{ overflowX: 'auto', marginBottom: 6 }}>
      <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
        <thead>
          <tr style={{ color: 'var(--text-muted)', textAlign: 'right' }}>
            <th style={{ textAlign: 'left', padding: '6px 8px' }}>Club</th>
            {weeks.map((w) => <th key={w} style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>Wk of {weekLabel(w)}</th>)}
            <th style={{ padding: '6px 8px' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {clubIds.map((c) => {
            const row = data.clubs[c];
            const tot = weeks.reduce((a, w) => ({ n: a.n + (row[w]?.n || 0), cents: a.cents + (row[w]?.cents || 0), blank: a.blank + (row[w]?.blankChecks || 0) }), { n: 0, cents: 0, blank: 0 });
            return (
              <tr key={c} style={{ borderTop: '1px solid var(--border)', textAlign: 'right' }}>
                <td style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700, color: 'var(--text)' }}>{CLUB_NAMES[c] || c}</td>
                {weeks.map((w) => {
                  const cell = row[w];
                  return (
                    <td key={w} style={{ padding: '6px 8px', color: cell ? 'var(--text)' : '#555', whiteSpace: 'nowrap' }}>
                      {cell ? <>{usd(cell.cents)} <span style={{ color: '#888' }}>· {cell.n}</span>
                        {cell.blankChecks > 0 && <div style={{ fontSize: 10, color: '#ef4444' }}>{cell.blankChecks} blank-check</div>}
                        {cell.labeledN > 0 && <div style={{ fontSize: 10, color: '#777' }}>+{cell.labeledN} explained</div>}</> : '–'}
                    </td>
                  );
                })}
                <td style={{ padding: '6px 8px', fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap' }}>
                  {usd(tot.cents)} <span style={{ color: '#888', fontWeight: 400 }}>· {tot.n}</span>
                  {tot.blank > 0 && <div style={{ fontSize: 10, color: '#ef4444', fontWeight: 400 }}>{tot.blank} blank-check</div>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PaymentCard({ p, onReview, updating }) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState(p.note || '');
  const free = p.chargedCents === 0;
  return (
    <div style={{
      background: 'var(--surface-2, #111)',
      border: `1px solid ${p.reviewStatus === 'flagged' ? '#ef444440' : 'var(--border, #222)'}`,
      borderRadius: 8, marginBottom: 8, padding: '10px 14px',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text, #e5e5e5)' }}>{p.name || '(not in COMS)'}</span>
            <span style={{ background: free ? '#ef444422' : '#f59e0b22', color: free ? '#ef4444' : '#f59e0b', border: `1px solid ${free ? '#ef444455' : '#f59e0b55'}`, borderRadius: 4, padding: '2px 6px', fontSize: 10, whiteSpace: 'nowrap' }}>
              {free ? `FREE (was ${usd2(p.listCents)})` : `${usd2(p.discountCents)} off ${usd2(p.listCents)}`}
            </span>
            {p.blankCheck && <span style={{ background: '#ef444422', color: '#ef4444', border: '1px solid #ef444455', borderRadius: 4, padding: '2px 6px', fontSize: 10 }}>blank check</span>}
            {p.autoFlag && <span style={{ background: '#ffffff11', color: '#aaa', border: '1px solid #333', borderRadius: 4, padding: '2px 6px', fontSize: 10 }}>{p.autoFlag === 'private_event' ? 'private event' : 'tagged staff/ambassador'}</span>}
            {p.welldStatus && <span style={{ background: '#14b8a622', color: '#14b8a6', border: '1px solid #14b8a655', borderRadius: 4, padding: '2px 6px', fontSize: 10 }}>{WELLD_LABEL[p.welldStatus]}{p.welldEndedOn ? ` (${p.welldEndedOn})` : ''}</span>}
            {p.joinedAfterDays !== null && p.joinedAfterDays !== undefined && <span style={{ background: '#22c55e22', color: '#22c55e', border: '1px solid #22c55e55', borderRadius: 4, padding: '2px 6px', fontSize: 10 }}>joined {p.joinedAfterDays < 1 ? 'the next day' : `${Math.round(p.joinedAfterDays)} days later`}</span>}
            {p.priorMember && !p.welldStatus && <span style={{ background: '#f59e0b22', color: '#f59e0b', border: '1px solid #f59e0b55', borderRadius: 4, padding: '2px 6px', fontSize: 10 }}>former member</span>}
            {p.membershipClass === 'member_comp' && <span style={{ background: '#ffffff11', color: '#aaa', border: '1px solid #333', borderRadius: 4, padding: '2px 6px', fontSize: 10 }}>comped member</span>}
          </div>
          <div style={{ fontSize: 11, color: '#777', marginBottom: 2 }}>
            {p.email || 'no email'} · {when(p.paidAt)} · {CLUB_NAMES[p.clubId] || p.clubId}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {p.category} — {p.item} — {p.tender}{p.coupon ? ` — coupon ${p.coupon}` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button disabled={updating} onClick={() => onReview(p.id, 'cleared', note)}
            style={{ background: p.reviewStatus === 'cleared' ? '#22c55e' : 'var(--surface-3, #1a1a1a)', color: p.reviewStatus === 'cleared' ? '#000' : '#22c55e', border: '1px solid #22c55e', borderRadius: 6, padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Legit</button>
          <button disabled={updating} onClick={() => onReview(p.id, 'flagged', note)}
            style={{ background: p.reviewStatus === 'flagged' ? '#ef4444' : 'var(--surface-3, #1a1a1a)', color: p.reviewStatus === 'flagged' ? '#000' : '#ef4444', border: '1px solid #ef4444', borderRadius: 6, padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Flag</button>
          <button onClick={() => setNoteOpen((o) => !o)}
            style={{ background: 'var(--surface-3, #1a1a1a)', color: 'var(--text-muted)', border: '1px solid var(--border, #333)', borderRadius: 6, padding: '5px 10px', fontSize: 11, cursor: 'pointer' }}>{noteOpen ? '▲' : '📝'}</button>
        </div>
      </div>
      {noteOpen && (
        <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
          <input className="field-input" type="text" placeholder="Reason / note..." value={note} onChange={(e) => setNote(e.target.value)} style={{ flex: 1, fontSize: 12 }} />
          <button disabled={updating} onClick={() => onReview(p.id, p.reviewStatus, note)}
            style={{ background: 'var(--yellow)', color: '#000', border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Save Note</button>
        </div>
      )}
      {p.note && !noteOpen && (
        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>"{p.note}"{p.reviewedBy ? ` — ${p.reviewedBy}` : ''}</div>
      )}
    </div>
  );
}

export default function StaffDiscounts() {
  const { get, post } = useApi();
  const { currentClub } = useClub();
  const [overview, setOverview] = useState(null);
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [klass, setKlass] = useState('nonmember');
  const [status, setStatus] = useState('pending');
  const [days, setDays] = useState(30);
  const [bucket, setBucket] = useState('review');
  const [welld, setWelld] = useState(null);
  const [updating, setUpdating] = useState({});

  const clubId = currentClub?.location_id || currentClub?.id;

  // get from useApi() is a new reference every render; leaving it out of the
  // dependency arrays below is deliberate (infinite fetch loop, confirmed live 9/30/26).
  useEffect(() => {
    get('/revenue/staff-discounts/overview?weeks=8').then(setOverview).catch((e) => setError(e.message));
    get('/revenue/staff-discounts/welld').then(setWelld).catch(() => setWelld({ total: 0, people: [] }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadList = useCallback(() => {
    setLoading(true);
    setError(null);
    get(`/revenue/staff-discounts?club=${clubId}&class=${klass}&status=${status}&days=${days}&bucket=${bucket}`)
      .then(setList)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [clubId, klass, status, days, bucket]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadList(); }, [loadList]);

  async function handleReview(paymentId, newStatus, note) {
    setUpdating((prev) => ({ ...prev, [paymentId]: true }));
    try {
      await post('/revenue/staff-discounts/review', { paymentId, status: newStatus, note });
      setList((prev) => prev ? ({ ...prev, payments: prev.payments.map((p) => p.id === paymentId ? { ...p, reviewStatus: newStatus, note } : p) }) : prev);
    } catch (e) {
      console.error('Staff discount review update failed', e);
    } finally {
      setUpdating((prev) => ({ ...prev, [paymentId]: false }));
    }
  }

  const payments = list?.payments || [];
  const visible = payments.filter((p) => status === 'all' || p.reviewStatus === status);
  const sumCents = visible.reduce((a, p) => a + p.discountCents, 0);

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
        🏷️ Discounts that <strong style={{ color: 'var(--text)' }}>staff</strong> applied to people with no membership on the day.
        Counts are the ones that still need a look. Payments explained by Welld, joining soon after, private events or tagged staff are set aside (shown as “+N explained”). Each cell is dollars · payments.
      </div>
      <div style={{ fontSize: 11, color: '#666', marginBottom: 12 }}>
        PlayByPoint does not record which staff member applied a discount, so this shows where and when, not who.
        Match the time to the shift schedule, or look the booking up in PlayByPoint admin.
      </div>

      {error && (
        <div style={{ padding: 12, color: 'var(--red)', fontSize: 12 }}>
          Failed to load — {error}. If this is the first time, the table may not exist yet.
          <button onClick={loadList} style={{ marginLeft: 12, background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '4px 12px', borderRadius: 6, cursor: 'pointer' }}>Retry</button>
        </div>
      )}

      <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)', margin: '10px 0 4px' }}>By club, last 8 weeks</div>
      {overview ? <Overview data={overview} /> : <div className="spinner" />}

      {overview?.repeaters?.length > 0 && (
        <div style={{ margin: '16px 0' }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)', marginBottom: 4 }}>
            Repeat recipients — {overview.repeatThreshold}+ discounted payments in 90 days (effectively free members)
          </div>
          {overview.repeaters.slice(0, 15).map((r) => (
            <div key={r.clubId + r.customerId} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '5px 0', borderTop: '1px solid var(--border)', fontSize: 12 }}>
              <span style={{ color: 'var(--text)' }}>{r.name || '(not in COMS)'} <span style={{ color: '#777' }}>· {r.email || 'no email'} · {CLUB_NAMES[r.clubId] || r.clubId}</span></span>
              <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap', textAlign: 'right' }}>
                {r.payments} payments · {usd(r.cents)}
                <div style={{ fontSize: 10, color: '#777' }}>first {when(r.firstAt)} · latest {when(r.lastAt)}</div>
              </span>
            </div>
          ))}
        </div>
      )}

      {welld?.people?.length > 0 && (
        <div style={{ margin: '16px 0' }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)', marginBottom: 2 }}>
            Welld re-enrollment — {welld.total} {welld.total === 1 ? 'person' : 'people'} with a Welld plan that expired (or none in PlayByPoint) and no membership now
          </div>
          <div style={{ fontSize: 11, color: '#666', marginBottom: 6 }}>
            Welld pays for visits only while the person has an active membership in PlayByPoint, so these visits may not be billed.
          </div>
          {welld.people.slice(0, 20).map((r) => (
            <div key={r.clubId + r.customerId} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '5px 0', borderTop: '1px solid var(--border)', fontSize: 12 }}>
              <span style={{ color: 'var(--text)' }}>{r.name || '(not in COMS)'} <span style={{ color: '#777' }}>· {r.email || 'no email'} · {CLUB_NAMES[r.clubId] || r.clubId}</span></span>
              <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap', textAlign: 'right' }}>
                {r.welldStatus === 'expired' ? `plan ended ${r.welldEndedOn || '?'}` : 'eligible, no plan'} · {r.payments} {r.payments === 1 ? 'visit' : 'visits'}
                <div style={{ fontSize: 10, color: '#777' }}>latest {when(r.lastAt)}</div>
              </span>
            </div>
          ))}
          {welld.total > 20 && <div style={{ fontSize: 11, color: '#777', marginTop: 4 }}>Showing 20 of {welld.total}.</div>}
        </div>
      )}

      <DownloadPanel clubId={clubId} get={get} />

      <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)', margin: '18px 0 8px' }}>
        Review list — {CLUB_NAMES[clubId] || clubId}
      </div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <button style={pill(klass === 'nonmember')} onClick={() => setKlass('nonmember')}>Non-members</button>
          <button style={pill(klass === 'comped')} onClick={() => setKlass('comped')}>Comped members</button>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {['pending', 'flagged', 'all'].map((s) => <button key={s} style={{ ...pill(status === s), textTransform: 'capitalize' }} onClick={() => setStatus(s)}>{s}</button>)}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {[7, 30, 90].map((d) => <button key={d} style={pill(days === d)} onClick={() => setDays(d)}>{d}d</button>)}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[['review', 'Needs review'], ['welld', 'Welld'], ['joined', 'Joined later'], ['private', 'Private / staff'], ['all', 'All']].map(([k, label]) => (
            <button key={k} style={pill(bucket === k)} onClick={() => setBucket(k)}>{label}</button>
          ))}
        </div>
      </div>

      {loading ? <div className="empty-state"><div className="spinner" /></div> : (
        <>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
            <strong style={{ color: 'var(--text)' }}>{visible.length}</strong> payments · {usd(sumCents)} discounted{payments.length >= 200 ? ' (showing the most recent 200)' : ''}
          </div>
          {visible.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: 32, fontStyle: 'italic' }}>
              Nothing to review for these filters.
            </div>
          ) : visible.map((p) => <PaymentCard key={p.id} p={p} onReview={handleReview} updating={updating[p.id]} />)}
        </>
      )}
    </div>
  );
}
